import canAutoPlay from "can-autoplay";
import { inject, injectable } from "inversify";
import { PlayerCTAAPI, ScencarioElementOptions } from "../../api/cta/PlayerCTAAPI";
import { VintomError } from "../../api/model/error/VintomError";
import { PlayerState } from "../../api/model/playerState/PlayerState";
import { CTA } from "../../model/playerConfiguration/CTA";
import { ScenarioElement } from "../../model/playerConfiguration/ScenarioElement";
import { IframeNotifierPlayerActionState } from "../../model/playerState/IframeNotifierPlayerActionState";
import { IframeNotifierPlayerReady } from "../../model/playerState/IframeNotifierPlayerReady";
import { IframeNotifierPlayerState } from "../../model/playerState/IframeNotifierPlayerState";
import { IframeNotifierVintomError } from "../../model/playerState/IframeNotifierVintomError";
import { ScenarioElementPlayOptions } from "../../model/scenarioElementPlayOptions/ScenarioElementPlayOptions";
import { ScenarioGlobalState } from "../../model/scenarioGlobalState/ScenarioGlobalState";
import { ScenarioPath } from "../../model/scenarioPath/ScenarioPath";
import { ListenerHelper } from "../../utils/ListenerHelper";
import { ConfigurationBuilder } from "../configurationBuilder/ConfigurationBuilder";
import { TYPES } from "../di/types";
import { ErrorReporter } from "../errorReporter/ErrorReporter";
import { IframeNotifier } from "../iframeNotifier/IframeNotifier";
import { PlayerLoader } from "../loader/playerLoader/PlayerLoader";
import { ScenarioLoader } from "../loader/scenarioLoader/ScenarioLoader";
import { AnalyticsCollector } from "../playerAnalytics/AnalyticsCollector";
import { PlayerScenarioRunnerAPI } from "./PlayerManager";
import { PreferencesStorage } from "../preferencesStorage/PreferencesStorage";
import { ControlsLayerManager } from "../scenarioRunner/layerManager/ControlsLayerManager";
import { PlayerContainerManager } from "../scenarioRunner/layerManager/PlayerContainerManager";
import { PosterLayerManager } from "../scenarioRunner/layerManager/PosterLayerManager";
import { VideoLayerManager } from "../scenarioRunner/layerManager/VideoLayerManager";
import { ScenarioRunner } from "../scenarioRunner/ScenarioRunner";
import { ThemeUpdater } from "../themeUpdater/ThemeUpdater";
import { PlayerMusicManager } from "../scenarioRunner/musicManager/PlayerSyncedMusicManager";
import { BasePlayerManager } from "./BasePlayerManager";
import { PlayerSetupConfig } from "../../api/player/PlayerConfigTypes";
import { BrowserFeatureDetector } from "../../utils/BrowserFeatureDetector";

@injectable()
export class PlayerManagerImpl extends BasePlayerManager implements PlayerCTAAPI, PlayerScenarioRunnerAPI {
  private static DEFAULT_SCENARIO_ELEMENT_PLAY_OPTIONS: ScenarioElementPlayOptions = {
    isAutoplay: false,
    progress: 0,
  };

  private static ANALYTICS_FASTER_RATE = 10;
  private static ANALYTICS_SLOWER_RATE = 20;
  private static ANALYTICS_SLOWER_RATE_THRESHOLD = 120;

  private static ANALYTICS_PROGRESS_25_THRESHOLD = 0.25;
  private static ANALYTICS_PROGRESS_50_THRESHOLD = 0.5;
  private static ANALYTICS_PROGRESS_75_THRESHOLD = 0.75;
  private static ANALYTICS_PROGRESS_100_THRESHOLD = 1;

  private isPlayerStarted: boolean;
  private analyticsIntervalHandle: number;
  private scenarioElementProgress: number;

  private onReadyCallbacks: ((payload: IframeNotifierPlayerReady) => void)[] = [];
  private onErrorCallbacks: ((error: IframeNotifierVintomError) => void)[] = [];
  private onPlayCallbacks: ((payload: IframeNotifierPlayerState) => void)[] = [];
  private onPauseCallbacks: ((payload: IframeNotifierPlayerState) => void)[] = [];
  private onFinishCallbacks: ((payload: IframeNotifierPlayerState) => void)[] = [];
  private onActionCallbacks: ((payload: IframeNotifierPlayerActionState) => void)[] = [];
  private newScenarioElementJustLoaded: boolean;

  constructor(
    @inject(TYPES.analyticsCollector.AnalyticsCollector) analyticsCollector: AnalyticsCollector,
    @inject(TYPES.configurationBuilder.ConfigurationBuilder) configurationBuilder: ConfigurationBuilder,
    @inject(TYPES.iframeNotifier.IframeNotifier) private iframeNotifier: IframeNotifier,
    @inject(TYPES.preferencesStorage.PreferencesStorage) private preferencesStorage: PreferencesStorage,
    @inject(TYPES.themeUpdater.ThemeUpdater) private themeUpdater: ThemeUpdater,
    @inject(TYPES.errorReporter.ErrorReporter) private errorReporter: ErrorReporter,
    @inject(TYPES.loader.playerLoader.PlayerLoader) private playerLoader: PlayerLoader,
    @inject(TYPES.loader.scenarioLoader.ScenarioLoader) private scenarioLoader: ScenarioLoader,
    @inject(TYPES.scenarioRunner.ScenarioRunner) private scenarioRunner: ScenarioRunner,
    @inject(TYPES.scenarioRunner.layerManager.ControlsLayerManager) private controlsLayerManager: ControlsLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.PlayerContainerManager) private playerContainerManager: PlayerContainerManager,
    @inject(TYPES.scenarioRunner.layerManager.PosterLayerManager) private posterLayerManager: PosterLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.VideoLayerManager) private videoLayerManager: VideoLayerManager,
    @inject(TYPES.music.MusicManager) private musicManager: PlayerMusicManager,
  ) {
    super(analyticsCollector, configurationBuilder);

    this.scenarioRunner.setPlayerAPIDelegate(this);
    this.scenarioLoader.setPlayerCTAAPIDelegate(this);
    this.registerIframeNotifier();
  }

  async initialize(config: PlayerSetupConfig) {
    await super.initialize(config);

    ListenerHelper.addEventListener("window", "beforeunload", () => this.onPlayerRemove());

    if (BrowserFeatureDetector.isMobileDevice()) {
      this.installAutoPause();
    }
  }

  run() {
    this.checkStatusAndRun().then(() => {
      // Ignored for now, in the future player will return a promise
    });
  }

  play(): void {
    this.scenarioRunner.play();
  }

  pause(): void {
    this.scenarioRunner.pause();
  }

  performAction(ctaId: number, actionId: number, ...args: any[]): void {
    this.scenarioRunner.performAction(ctaId, actionId, args);
  }

  moveToScenarioElement(id: string): void {
    const scenarioElement = this.playerConfig.findScenarioElement(id);
    if (scenarioElement) {
      this.loadScenarioElement(scenarioElement);
    }
  }

  prepareForRemove(): void {
    ListenerHelper.removeAllEventListeners();
    this.scenarioLoader.prepareForRemove();
    this.onPlayerRemove();
    this.clearAnalyticsInterval();
  }

  registerOnReady(callback: () => void): void {
    this.onReadyCallbacks.push(callback);
  }

  registerOnError(callback: (error: IframeNotifierVintomError) => void): void {
    this.onErrorCallbacks.push(callback);
  }

  registerOnPlay(callback: (payload: IframeNotifierPlayerState) => void): void {
    this.onPlayCallbacks.push(callback);
  }

  registerOnPause(callback: (payload: IframeNotifierPlayerState) => void): void {
    this.onPauseCallbacks.push(callback);
  }

  registerOnFinish(callback: (payload: IframeNotifierPlayerState) => void): void {
    this.onFinishCallbacks.push(callback);
  }

  registerOnAction(callback: (payload: IframeNotifierPlayerActionState) => void): void {
    this.onActionCallbacks.push(callback);
  }

  onReady(): void {
    this.analyticsCollector.logPlayerInit(this.getCurrentElementId());
    const readyState: IframeNotifierPlayerReady = {
      code: this.playerConfig.code,
      type: "vintomPlayerReady",
    };
    this.onReadyCallbacks.forEach((cb) => cb(readyState));
  }

  onError(error: VintomError): void {
    const isConfigurationParsed = !!this.playerConfig;
    if (!isConfigurationParsed) {
      console.error(error);
      return;
    }

    // TODO Better error reporting in dev mode
    if (this.playerConfig.adminMode) {
      console.trace();
      console.error(error);
    }

    const { isLogAndSendToAnalytics, isDisplayToUser } = error.options ?? {};

    if (isLogAndSendToAnalytics) {
      const iframeError = error as IframeNotifierVintomError;
      iframeError.code = this.playerConfig.code;
      iframeError.type = "vintomPlayerError";
      this.onErrorCallbacks.forEach((cb) => {
        cb(iframeError);
      });
      this.analyticsCollector.logPlayerError(this.getCurrentElementId(), error);
    }

    if (isDisplayToUser) {
      this.controlsLayerManager.showMessage(error);
    }
  }

  onPlay(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();

    if (!playbackData.isPlaying) {
      return;
    }

    if (!this.isPlayerStarted) {
      this.analyticsCollector.logPlayerStart(this.getCurrentElementId());
      this.isPlayerStarted = true;
    }

    if (this.newScenarioElementJustLoaded) {
      this.onScenarioElementStart();
      this.newScenarioElementJustLoaded = false;
    }

    this.analyticsCollector.logScenarioElementPlaying(
      this.getCurrentElementId(),
      playbackData.videoType,
      playbackData.volume,
      playbackData.duration,
      playbackData.time,
    );

    const state = this.getState() as IframeNotifierPlayerState;
    state.code = this.playerConfig.code;
    state.type = "vintomPlayerPlay";
    this.onPlayCallbacks.forEach((cb) => cb(state));
  }

  onPause(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();
    this.analyticsCollector.logScenarioElementPlaying(
      this.getCurrentElementId(),
      playbackData.videoType,
      playbackData.volume,
      playbackData.duration,
      playbackData.time,
      "PAUSED",
    );

    const state = this.getState() as IframeNotifierPlayerState;
    state.code = this.playerConfig.code;
    state.type = "vintomPlayerPause";
    this.onPauseCallbacks.forEach((cb) => cb(state));
  }

  onSeek(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();
    this.analyticsCollector.logScenarioElementPlaying(
      this.getCurrentElementId(),
      playbackData.videoType,
      playbackData.volume,
      playbackData.duration,
      playbackData.time,
      "SEEKED",
    );
  }

  onProgress(progress: number): void {
    if (
      this.scenarioElementProgress < PlayerManagerImpl.ANALYTICS_PROGRESS_25_THRESHOLD &&
      progress >= PlayerManagerImpl.ANALYTICS_PROGRESS_25_THRESHOLD
    ) {
      this.analyticsCollector.logScenarioElementProgress(this.getCurrentElementId(), 25);
    }
    if (
      this.scenarioElementProgress < PlayerManagerImpl.ANALYTICS_PROGRESS_50_THRESHOLD &&
      progress >= PlayerManagerImpl.ANALYTICS_PROGRESS_50_THRESHOLD
    ) {
      this.analyticsCollector.logScenarioElementProgress(this.getCurrentElementId(), 50);
    }
    if (
      this.scenarioElementProgress < PlayerManagerImpl.ANALYTICS_PROGRESS_75_THRESHOLD &&
      progress >= PlayerManagerImpl.ANALYTICS_PROGRESS_75_THRESHOLD
    ) {
      this.analyticsCollector.logScenarioElementProgress(this.getCurrentElementId(), 75);
    }
    if (
      this.scenarioElementProgress < PlayerManagerImpl.ANALYTICS_PROGRESS_100_THRESHOLD &&
      progress >= PlayerManagerImpl.ANALYTICS_PROGRESS_100_THRESHOLD
    ) {
      this.analyticsCollector.logScenarioElementProgress(this.getCurrentElementId(), 100);
    }

    this.scenarioElementProgress = progress;
  }

  onScenarioElementEnd(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();
    this.analyticsCollector.logScenarioElementPlaying(
      this.getCurrentElementId(),
      playbackData.videoType,
      playbackData.volume,
      playbackData.duration,
      playbackData.time,
      "FINISHED",
    );

    this.clearAnalyticsInterval();

    if (this.getScenarioPath().isLastPart) {
      const state = this.getState() as IframeNotifierPlayerState;
      state.code = this.playerConfig.code;
      state.type = "vintomPlayerFinish";
      this.onFinishCallbacks.forEach((cb) => cb(state));
    } else {
      this.loadNextScenarioElement();
    }
  }

  onCTAShow(cta: CTA): void {
    if (!this.isTechnicalCTA(cta)) {
      this.analyticsCollector.logCTAShow(this.getCurrentElementId(), cta.id);
    }
  }

  isTechnicalCTA(cta: CTA): boolean {
    return cta.triggerType === "RANGE" && cta.visibilityRange && cta.visibilityRange.startTime === 0;
  }

  onCTAAction(ctaId: number, actionId: number, actionAdditionalData: object): void {
    const state = this.getState();
    const actionState: IframeNotifierPlayerActionState = {
      ...state,
      ctaId,
      actionId,
      actionAdditionalData,
      code: this.playerConfig.code,
      type: "vintomPlayerAction",
    };
    this.analyticsCollector.logCTAAction(this.getCurrentElementId(), ctaId, actionId, actionAdditionalData);
    this.onActionCallbacks.forEach((cb) => cb(actionState));
  }

  onPlayerRemove(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();
    if (playbackData) {
      this.analyticsCollector.logScenarioElementPlaying(
        this.getCurrentElementId(),
        playbackData.videoType,
        playbackData.volume,
        playbackData.duration,
        playbackData.time,
        "CLOSED",
      );
    } else {
      // player may be removed before it's initialized
      this.analyticsCollector.logScenarioElementPlaying(this.getCurrentElementId(), undefined, undefined, undefined, undefined, "CLOSED");
    }
  }

  getState(): PlayerState {
    const playbackData = this.scenarioRunner.getPlaybackData();

    const state: PlayerState = {
      playing: playbackData.isPlaying,
      time: playbackData.time,
    };

    if (!playbackData) {
      return undefined;
    }

    if (this.playerConfig.scenarioElements.length > 1) {
      state.scenarioElementId = this.getCurrentElementId();
    }

    if (this.getScenarioPath().hasMultipleParts) {
      state.part = this.getScenarioPath().currentPartNumber;
    }

    return state;
  }

  loadNextScenarioElement() {
    const nextId = this.getScenarioPath().currentElement.nextScenarioElementId;
    if (nextId !== undefined) {
      const nextElement = this.playerConfig.findScenarioElement(nextId);
      if (nextElement) {
        this.loadScenarioElement(nextElement, { isAutoplay: true });
      }
    }
  }

  loadScenarioElement(scenarioElement: ScenarioElement, options: ScenarioElementPlayOptions = {}) {
    options = {
      ...PlayerManagerImpl.DEFAULT_SCENARIO_ELEMENT_PLAY_OPTIONS,
      ...options,
      loop: scenarioElement.loop,
    };

    this.newScenarioElementJustLoaded = true;
    this.loadMusicForScenarioElement(scenarioElement);

    // this.controlsLayerManager.showLoader();

    const scenarioResources = this.scenarioLoader.loadResources(this.playerConfig, scenarioElement);
    scenarioResources.catch((e) => this.onError(e));
    const scenarioGlobalState = this.scenarioRunner.getScenarioGlobalState() || this.getInitialScenarioGlobalState();

    return this.scenarioRunner.run(scenarioGlobalState, scenarioElement, scenarioResources, options);
  }

  jumpToScenarioElement(scenarioElementId: string, options?: ScencarioElementOptions) {
    const progress = (options && options.progress) || 0;
    const start = options && options.start !== undefined ? options.start : true;

    const scenarioElement = this.playerConfig.findScenarioElement(scenarioElementId);
    if (scenarioElement) {
      this.scenarioRunner.jumpToScenarioElement(scenarioElement, progress, start);
    } else {
      this.onError(new VintomError("runtimeError", `Attempted to load non existing scenario element (id: ${scenarioElementId})`));
    }
  }

  replay() {
    this.loadScenarioElement(this.getScenarioPath().initialElement, {
      isAutoplay: true,
    });
  }

  logUserError(error: VintomError): void {
    this.errorReporter.sendErrorLog(error);
  }

  getMusicManager(): PlayerMusicManager {
    return this.musicManager;
  }

  private registerIframeNotifier() {
    this.onReadyCallbacks.push(this.iframeNotifier.notify);
    this.onErrorCallbacks.push(this.iframeNotifier.notify);
    this.onPlayCallbacks.push(this.iframeNotifier.notify);
    this.onPauseCallbacks.push(this.iframeNotifier.notify);
    this.onFinishCallbacks.push(this.iframeNotifier.notify);
    this.onActionCallbacks.push(this.iframeNotifier.notify);
  }

  private getInitialScenarioGlobalState(): ScenarioGlobalState {
    const storedPreferences = this.preferencesStorage.getPreferences();
    return ScenarioGlobalState.initialState(
      this.playerConfig.code,
      this.playerConfig.campaign,
      this.playerConfig.scenarioElements,
      this.playerConfig.parts,
      this.playerConfig.partsDebugInfo,
      this.playerConfig.aspectRatio,
      this.playerConfig.mutedStart,
      this.playerConfig.subtitlesActiveOnStart,
      storedPreferences,
    );
  }

  private getScenarioPath(): ScenarioPath {
    const scenarioGlobalState = this.scenarioRunner.getScenarioGlobalState();
    return scenarioGlobalState && scenarioGlobalState.scenarioPath;
  }

  private getCurrentElementId(): string {
    const scenarioPath = this.getScenarioPath();
    return scenarioPath && scenarioPath.currentElementId;
  }

  private async checkStatusAndRun() {
    this.controlsLayerManager.setConfiguration(this.playerConfig.playerControls, this.playerConfig.adminMode);
    this.playerContainerManager.setConfiguration(this.playerConfig.playerControls);

    switch (this.playerConfig.status) {
      case "DELETED":
        await this.playerLoader.loadResources(this.playerConfig);
        this.controlsLayerManager.showMessage(this.i18n.videoDeleted);

        return;
      case "PREPARING":
        await this.playerLoader.loadResources(this.playerConfig);
        this.controlsLayerManager.showMessage(this.i18n.videoInPreparation);

        return;
      case "READY":
        return this.startPlayer();
      default:
        throw new VintomError("runtimeError", `Unsupported film status '${status}'`);
    }
  }

  private startPlayer(): Promise<void> {
    const initialScEl = this.playerConfig.getInitialScenarioElement();

    this.controlsLayerManager.showLoader("runStart");
    this.posterLayerManager.showInitialPoster(initialScEl.poster, this.playerConfig.aspectRatio);

    return this.loadAndInitializeLayers()
      .then(() => {
        if (this.playerConfig.autoplay || (initialScEl.isCTAOnlyElement && !initialScEl.isCTAOnlyPlaybackStoppingElement)) {
          return this.checkAutoplaySupported();
        }
        return false;
      })
      .then((isAutoplay) => this.loadScenarioElement(initialScEl, { isAutoplay }))
      .then(() => this.themeUpdater.setTheme(this.playerConfig.skin))
      .then(() => this.initializeMusicManager())
      .then(() => this.controlsLayerManager.hideLoader())
      .then(() => this.onReady());
  }

  private loadAndInitializeLayers(): Promise<void> {
    return this.playerLoader.loadResources(this.playerConfig).then(() => {
      if (this.playerConfig.hasScenarioElementWithVideo) {
        this.videoLayerManager.initializeLayer();
      }
    });
  }

  private clearAnalyticsInterval() {
    if (this.analyticsIntervalHandle !== undefined) {
      clearInterval(this.analyticsIntervalHandle);
    }
  }

  private startCollectingAnalyticsAtInterval() {
    this.clearAnalyticsInterval();

    const duration = this.scenarioRunner.getPlaybackData().duration;
    const interval =
      duration < PlayerManagerImpl.ANALYTICS_SLOWER_RATE_THRESHOLD
        ? PlayerManagerImpl.ANALYTICS_FASTER_RATE
        : PlayerManagerImpl.ANALYTICS_SLOWER_RATE;

    this.analyticsIntervalHandle = window.setInterval(() => {
      const playbackData = this.scenarioRunner.getPlaybackData();
      if (playbackData.isPlaying) {
        this.analyticsCollector.logScenarioElementPlaying(
          this.getCurrentElementId(),
          playbackData.videoType,
          playbackData.volume,
          playbackData.duration,
          playbackData.time,
        );
      }
    }, interval * 1000);
  }

  private checkAutoplaySupported(): Promise<boolean> {
    return canAutoPlay
      .video()
      .then(({ result }) => result)
      .catch(() => false);
  }

  private installAutoPause(): void {
    let shouldResume = false;

    const onBlur = () => {
      shouldResume = this.videoLayerManager.getIsPlaying();

      this.pause();
    };

    const onFocus = () => {
      if (shouldResume) {
        this.play();
      }
    };

    ListenerHelper.addEventListener("document", "visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        onBlur();
      } else {
        onFocus();
      }
    });
  }

  private onScenarioElementStart(): void {
    const playbackData = this.scenarioRunner.getPlaybackData();

    this.analyticsCollector.logScenarioElementStart(
      this.getCurrentElementId(),
      playbackData.videoType,
      playbackData.volume,
      playbackData.duration,
    );

    this.startCollectingAnalyticsAtInterval();
  }

  private loadMusicForScenarioElement(scenarioElement: ScenarioElement) {
    if (scenarioElement.music) {
      if (this.playerConfig.scenarioMusicUrl && scenarioElement.music.musicUrl) {
        console.warn("Scenario elements shouldn't have musicUrl if scenario has scenarioMusicUrl");
      } else if (!this.playerConfig.scenarioMusicUrl) {
        this.musicManager.src = scenarioElement.music.musicUrl;
      }

      this.musicManager.loadSoundtrackTransitions(scenarioElement.music);
    } else {
      this.musicManager.resetTransitions();
    }
  }

  private initializeMusicManager() {
    if (this.playerConfig.scenarioMusicUrl) {
      this.musicManager.src = this.playerConfig.scenarioMusicUrl;
      this.musicManager.syncOnPlay = false;
    }
  }
}
